import type { AstInput } from '#core/lib/expr/normalize.ts';
import { normalize } from '#core/lib/expr/normalize.ts';
import { parse } from '#core/lib/expr/parser.ts';
import type { Session } from '#core/lib/expr/session/Session.ts';
import type { Value } from '#core/lib/expr/value.ts';

/**
 * Tool-layer dataset-binding SSOT.
 *
 * A producer tool (`queryKnowledgeGraph`, `transformDataset`) records
 * its result under `outputDataset` as an immutable replay recipe so
 * downstream tools reference it by name and a reload rehydrates it.
 *
 * **Why staging, not a direct write:** `session.recordBinding` mutates
 * the conversation store and (in the chat impl) synchronously persists
 * to localStorage with no rollback path. If a producer recorded inside
 * its `execute` and then *anything* afterwards threw — `summarizeDataset`,
 * schema mapping, or the harness judging the call failed — the LLM is
 * told the call failed while a binding stays committed and persisted.
 * The next call reuses the name and hits `SessionRebindingError`,
 * forcing the agent into `_v2` guessing (the reported transcript bug).
 * Careful ordering narrows that window; it cannot close it.
 *
 * So producers **stage** the binding onto their success envelope; the
 * `<Tool>` execute boundary commits it **iff** that envelope is a
 * `success: true` result, as the final step before returning. A call
 * the LLM is told failed can therefore never leave or persist a
 * binding, by construction — independent of producer-side ordering.
 */
export const recordDatasetBinding = (session: Session, name: string, expression: string, value: Value): void => {
  session.recordBinding(name, expressionToAst(expression), value);
};

/**
 * Turn a producer's `expression` string into a `Recipe.exprAst` — the
 * SSOT for "producing s-expr → binding AST". A tool call is a single
 * top-level form; the parser emits no Date/Dataset literals, so the
 * `AstInput` is JSON-safe. Shared by the live commit path
 * (`recordDatasetBinding`) and the active-branch projection
 * (`recipesFromMessages`) so both reconstruct identical recipes.
 */
export const expressionToAst = (expression: string): AstInput => normalize(parse(expression)).ast[0] ?? null;

/** Internal marker carrying a deferred binding on a success envelope.
 * A `Symbol` so it is invisible to JSON serialisation (the transcript)
 * and to the `success`-marker checks in `parseResultEnvelope`. */
const PENDING_BINDING = Symbol('pendingDatasetBinding');

type PendingBinding = {
  readonly session: Session;
  readonly name: string;
  readonly expression: string;
  readonly value: Value;
};

const isObject = (v: unknown): v is object => typeof v === 'object' && v !== null;

const getPending = (result: unknown): PendingBinding | undefined =>
  isObject(result) && PENDING_BINDING in result
    ? (result as Record<symbol, PendingBinding>)[PENDING_BINDING]
    : undefined;

/**
 * Attach a deferred dataset-binding intent to a producer's success
 * envelope. Nothing is written to the session here — the `<Tool>`
 * boundary commits it only on a success result. Returns the same
 * envelope for `return stageDatasetBinding({ success: true, … }, …)`.
 */
export const stageDatasetBinding = <T extends object>(
  envelope: T,
  session: Session,
  name: string,
  expression: string,
  value: Value,
): T => {
  Object.defineProperty(envelope, PENDING_BINDING, {
    value: { session, name, expression, value } satisfies PendingBinding,
    enumerable: false,
    configurable: true,
  });
  return envelope;
};

/**
 * Commit a staged binding iff one is present, then strip the marker so
 * it never reaches the transcript. Called by the `<Tool>` execute
 * boundary after a producer returns a **success** envelope. Re-throws
 * `SessionRebindingError` (the binding store rejects a divergent
 * duplicate *before* mutating, so nothing is committed on clash) for the
 * boundary to normalise into the structured error envelope.
 */
export const commitPendingBinding = (result: unknown): unknown => {
  const pending = getPending(result);
  if (pending === undefined) {
    return result;
  }
  recordDatasetBinding(pending.session, pending.name, pending.expression, pending.value);
  Reflect.deleteProperty(result as object, PENDING_BINDING);
  return result;
};

/** Drop a staged binding without committing — the result was not a
 * success envelope, so the producer's call is considered failed. */
export const stripPendingBinding = (result: unknown): unknown => {
  if (isObject(result) && PENDING_BINDING in result) {
    Reflect.deleteProperty(result, PENDING_BINDING);
  }
  return result;
};
